import { useUiConfig } from '@/config/uiConfigHooks'
import { Button } from '@helix-ds/react'
import type {
  OrderSuccessButtonAction,
  OrderSuccessConfig,
} from '@/config/uiConfig'
import { resolveOrderSuccessButtons } from '@/config/uiConfig'
import type { SignatureRequestedCopy } from '@/pages/order/orderSuccessSigningCopy'
import { sanitizeRichHtml } from '@/utils/sanitizeRichHtml'
import { Close, FilePen, Truck } from 'flowbite-react-icons/outline'
import { useMemo } from 'react'

export interface OrderSuccessModalProps {
  isOpen: boolean
  /** Default close behavior (legacy ``navigateInProgress`` action). */
  onClose: () => void
  /**
   * Optional handler for the ``returnToDashboard`` action. Falls back to the
   * default close handler when not provided.
   */
  onReturnToDashboard?: () => void
  /**
   * Optional handler for the ``printOrderForm`` action. Falls back to the
   * default close handler when not provided. Caller should open the
   * requisition / trigger ``window.print()``.
   */
  onPrintOrderForm?: () => void
  /**
   * Optional handler for the ``generateShippingLabel`` action. Falls back to
   * the default close handler when not provided. Caller should request the kit
   * shipping label and open the ShippingLabelModal.
   */
  onGenerateShippingLabel?: () => void
  /** When true (EHR/Epic flow), use EHR-specific message; when false, use the standalone copy. */
  isEhrFlow?: boolean
  /**
   * When the submit emailed the signer, pass the resolved title/subtitle and
   * Next Steps here to override the tenant ``orderSuccess`` title/subtitle and
   * replace the body with those Next Steps paragraphs.
   * Does not affect button rendering on its own — pass ``variant='signature-requested'``
   * to also collapse the footer to the single centred action.
   * When null or omitted, the modal renders today's tenant copy unchanged.
   */
  signatureRequestedCopy?: SignatureRequestedCopy | null
  /**
   * When ``'signature-requested'``, the modal renders the document-pen icon,
   * collapses the footer to a single centred "Return to Dashboard" outline
   * pill (suppressing all tenant-configured buttons), and widens the panel
   * to 512 px. Absent or any other value → generic order-success behaviour,
   * unchanged.
   */
  variant?: 'signature-requested'
}

const DEFAULT_LEGACY_BUTTON_LABEL = 'Understood'

/** Outlined button (variant ``secondary``). */
const SECONDARY_BUTTON_CLASS =
  'min-h-10 rounded-full border border-border-base bg-white px-5 py-2.5 text-sm font-medium text-text-heading shadow-xs hover:bg-background-secondary-medium focus:outline-none focus:ring-2 focus:ring-brand-600 dark:border-gray-700 dark:bg-gray-900 dark:text-white dark:hover:bg-gray-800'

export function OrderSuccessModal({
  isOpen,
  onClose,
  onReturnToDashboard,
  onPrintOrderForm,
  onGenerateShippingLabel,
  isEhrFlow = false,
  signatureRequestedCopy = null,
  variant,
}: OrderSuccessModalProps) {
  const { config } = useUiConfig()
  const providerConsent = config.providerConsent
  const orderSuccess: OrderSuccessConfig | undefined = providerConsent?.orderSuccess

  const title = signatureRequestedCopy
    ? signatureRequestedCopy.title
    : orderSuccess?.title?.trim() ||
      providerConsent?.orderSuccessTitle ||
      'Your order has been submitted to the lab.'

  const subtitle = signatureRequestedCopy
    ? signatureRequestedCopy.subtitle
    : (orderSuccess?.subtitle?.trim() ?? '')

  // Body precedence:
  // 1. When signatureRequestedCopy is set, show its Next Steps paragraphs.
  // 2. New configurable HTML body (sanitized).
  // 3. Legacy ``orderSuccessMessage`` (plain text) — honored in BOTH flows now;
  //    previously the standalone flow ignored this and hard-coded a string.
  // 4. Flow-specific default copy.
  const legacyMessage =
    providerConsent?.orderSuccessMessage?.trim() ||
    (isEhrFlow ? 'You may close this tab to go back to the EHR.' : 'You may now leave this order.')

  const bodyHtml = orderSuccess?.bodyHtml?.trim()
  const safeBodyHtml = useMemo(
    () => (bodyHtml ? sanitizeRichHtml(bodyHtml) : ''),
    [bodyHtml]
  )

  const hasStructuredOrderSuccess = orderSuccess !== undefined
  const legacyButtonLabel =
    providerConsent?.orderSuccessButtonLabel?.trim() || DEFAULT_LEGACY_BUTTON_LABEL

  // Resolve the ordered button list (CONTRACT_MULTIBUTTON §2). When
  // ``orderSuccess.buttons`` is present it is authoritative (in array order,
  // skipping disabled/unknown-action entries, ``[]`` meaning "no buttons").
  // When absent, the legacy primaryButton/secondaryButton pair resolves exactly
  // as before — secondary rendered first, then primary.
  const resolvedButtons = useMemo(
    () =>
      resolveOrderSuccessButtons(orderSuccess, {
        hasStructuredOrderSuccess,
        legacyButtonLabel,
      }),
    [orderSuccess, hasStructuredOrderSuccess, legacyButtonLabel]
  )

  const runAction = (action: OrderSuccessButtonAction) => {
    switch (action) {
      case 'printOrderForm':
        if (onPrintOrderForm) {
          onPrintOrderForm()
          return
        }
        onClose()
        return
      case 'generateShippingLabel':
        if (onGenerateShippingLabel) {
          onGenerateShippingLabel()
          return
        }
        onClose()
        return
      case 'returnToDashboard':
        if (onReturnToDashboard) {
          onReturnToDashboard()
          return
        }
        onClose()
        return
      case 'closeModal':
        onClose()
        return
      case 'navigateInProgress':
      default:
        onClose()
    }
  }

  const isSignatureRequested = variant === 'signature-requested'

  if (!isOpen) {
    return null
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <button
        type="button"
        aria-label="Close success message"
        onClick={onClose}
        className="absolute inset-0 bg-black/40"
      />
      <div
        className={`relative w-full rounded-base border border-border-base bg-white px-6 py-7 text-center shadow-xl ${isSignatureRequested ? 'max-w-lg' : 'max-w-md'} dark:border-gray-700 dark:bg-gray-900`}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 flex size-9 items-center justify-center rounded-full text-text-body-subtle hover:bg-background-tertiary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 dark:text-gray-500 dark:hover:bg-gray-800"
        >
          <Close className="h-5 w-5" />
        </button>
        <div
          className="mx-auto mb-3 flex size-10 items-center justify-center text-text-heading dark:text-white"
          data-testid={isSignatureRequested ? 'icon-document-pen' : 'icon-truck'}
        >
          {isSignatureRequested ? (
            <FilePen className="h-8 w-8" aria-hidden="true" />
          ) : (
            <Truck className="h-8 w-8" aria-hidden="true" />
          )}
        </div>
        <h2 className="text-xl font-semibold tracking-tight text-text-heading sm:text-2xl dark:text-white">
          {title}
        </h2>
        {subtitle && (
          <p className="mt-2 text-sm text-text-body dark:text-gray-400">{subtitle}</p>
        )}

        {signatureRequestedCopy ? (
          <div className="mt-4 rounded-base bg-brand-50 px-4 py-3 text-left text-sm text-text-body dark:bg-gray-800 dark:text-gray-400">
            <p className="mb-2 text-base font-semibold text-text-heading dark:text-white">Next Steps</p>
            {signatureRequestedCopy.nextSteps.map((p) => (
              <p key={p.heading} className="my-2">
                <strong className="font-semibold text-text-heading dark:text-white">{p.heading}</strong>{' '}
                {p.body}
              </p>
            ))}
          </div>
        ) : safeBodyHtml ? (
          <div
            className="mt-4 rounded-base bg-brand-50 px-4 py-3 text-left text-sm text-text-body [&_h1]:mb-2 [&_h1]:text-base [&_h1]:font-semibold [&_h1]:text-text-heading [&_h2]:mb-2 [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:text-text-heading [&_li]:my-1 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-2 [&_strong]:font-semibold [&_strong]:text-text-heading [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 dark:bg-gray-800 dark:text-gray-400 dark:[&_h1]:text-white dark:[&_h2]:text-white dark:[&_strong]:text-white"
            dangerouslySetInnerHTML={{ __html: safeBodyHtml }}
          />
        ) : (
          <p className="mt-3 text-base leading-5 text-text-body dark:text-gray-400">{legacyMessage}</p>
        )}

        {isSignatureRequested ? (
          <div className="mt-6 flex justify-center">
            <button
              type="button"
              onClick={() => runAction('returnToDashboard')}
              className={SECONDARY_BUTTON_CLASS}
            >
              Return to Dashboard
            </button>
          </div>
        ) : (
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            {resolvedButtons.map((button, index) => (
              <Button
                key={`${button.action}-${index}`}
                variant={button.variant === 'primary' ? 'primary' : 'secondary'}
                size="md"
                type="button"
                onClick={() => runAction(button.action)}
                className="min-h-10 shadow-xs focus-visible:ring-brand-600"
              >
                {button.label}
              </Button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
